import React, { useState, useEffect } from 'react';
import { DATE_FORMAT } from 'common/data/constants';
import { formatDate } from 'helpers/formatDate';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Button, Badge } from 'reactstrap';

export const ApplicantNotes = ({
  userId,
  userName,
  initialNotes = [],
  onChange,
  withBackground = true,
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = useState(initialNotes);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  const addNote = text => {
    let cleanedText = text.replace(/\n\s*\n/g, '\n').trim();
    if (!cleanedText) return;

    const newNotes = [
      ...notes,
      {
        id: null,
        author: { id: userId, userName: userName },
        text: cleanedText,
        created_at: new Date().toISOString().replace('T', ' ').slice(0, 19),
      },
    ];

    setNotes(newNotes);
    onChange?.(newNotes);
  };

  const updateNote = () => {
    let cleanedText = editText.replace(/\n\s*\n/g, '\n').trim();
    if (!cleanedText || editingIndex === null) return;

    const updatedNotes = [...notes];
    updatedNotes[editingIndex] = {
      ...updatedNotes[editingIndex],
      text: cleanedText,
    };

    setNotes(updatedNotes);
    onChange?.(updatedNotes);
    setEditingIndex(null);
    setEditText('');
  };

  const startEdit = (note, index) => {
    if (note.id === null) {
      setEditingIndex(index);
      setEditText(note.text);
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const deleteNote = index => {
    const note = notes[index];
    if (note.id === null) {
      const updatedNotes = notes.filter((_, i) => i !== index);
      setNotes(updatedNotes);
      onChange?.(updatedNotes);

      if (editingIndex === index) {
        setEditingIndex(null);
        setEditText('');
      }
    }
  };

  const handleAddNote = () => {
    if (newNoteText.trim()) {
      addNote(newNoteText);
      setNewNoteText('');
      setShowInput(false);
    }
  };

  const noteCount = notes.length;

  return (
    <Card
      className="border-0"
      style={{ backgroundColor: withBackground ? '#f9f9f9' : 'transparent' }}
    >
      {/* Accordion Header */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: 'pointer',
          padding: '16px',
          backgroundColor: withBackground ? '#f9f9f9' : 'transparent',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <i className="bx bx-note me-2" style={{ fontSize: '1.25rem' }} />
          <strong>{t('order.notes')}</strong>
          {noteCount > 0 && (
            <Badge color="primary" pill className="ms-2">
              {noteCount}
            </Badge>
          )}
        </div>
        <i className={`bx bx-chevron-${isOpen ? 'up' : 'down'} fs-4`} />
      </div>

      {/* Accordion Body */}
      {isOpen && (
        <CardBody className="pt-0">
          {notes.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bx bx-note fs-1 mb-2" />
              <p className="mb-0">{t('order.noNotesYet')}</p>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Vertical Timeline Line */}
              <div
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '0',
                  bottom: '0',
                  width: '2px',
                  backgroundColor: '#e0e0e0',
                  zIndex: 0,
                }}
              />

              {/* Timeline Notes */}
              {notes.map((note, index) => {
                const isLatest = index === notes.length - 1;
                const isEditing = editingIndex === index;

                return (
                  <div
                    key={note.id || index}
                    className="d-flex mb-4 position-relative"
                    style={{ zIndex: 1 }}
                  >
                    {/* Timeline Dot */}
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: isLatest ? '#0d6efd' : '#f8f9fa',
                        border: `2px solid ${isLatest ? '#0d6efd' : '#dee2e6'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        background: isLatest ? '#0d6efd' : 'white',
                      }}
                    >
                      <i
                        className={`bx ${
                          isLatest
                            ? 'bx-check-double text-white'
                            : 'bx-note text-muted'
                        }`}
                        style={{ fontSize: '1.25rem' }}
                      />
                    </div>

                    {/* Content */}
                    <Card
                      className="flex-grow-1 ms-3 shadow-sm"
                      style={{
                        borderLeft: isLatest
                          ? '3px solid #0d6efd'
                          : '1px solid #e0e0e0',
                        backgroundColor: isEditing ? '#fffef7' : '#fff',
                      }}
                    >
                      <CardBody className="p-3">
                        {isEditing ? (
                          // Edit Mode
                          <div>
                            <textarea
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              autoFocus
                              rows={4}
                              maxLength={255}
                              style={{
                                width: '100%',
                                padding: '8px',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                marginBottom: '4px',
                              }}
                            />
                            <div
                              style={{
                                fontSize: '11px',
                                color:
                                  editText.length === 255 ? '#f44336' : '#999',
                                textAlign: 'right',
                                marginBottom: '8px',
                              }}
                            >
                              {editText.length} / 255 {t('common.characters')}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Button
                                onClick={cancelEdit}
                                size="sm"
                                color="secondary"
                                outline
                              >
                                {t('common.cancel')}
                              </Button>
                              <Button
                                onClick={updateNote}
                                disabled={!editText.trim()}
                                size="sm"
                                color="primary"
                              >
                                {t('common.update')}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div>
                            <div
                              style={{
                                fontSize: '14px',
                                marginBottom: '8px',
                                whiteSpace: 'pre-line',
                                cursor:
                                  note.id === null ? 'pointer' : 'default',
                              }}
                              onClick={() => startEdit(note, index)}
                            >
                              {note.text}
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-2">
                              <div className="d-flex gap-3">
                                <span className="text-muted small">
                                  <i className="bx bx-calendar me-1" />
                                  {formatDate(note.created_at, DATE_FORMAT)}
                                </span>
                                <span className="text-muted small">
                                  <i className="bx bx-user me-1" />
                                  {note.author.userName}
                                </span>
                              </div>
                              <div className="d-flex gap-3">
                                {note.id === null && !isEditing && (
                                  <i
                                    className="bx bx-pencil text-muted"
                                    style={{ cursor: 'pointer', opacity: 0.6 }}
                                    onClick={() => startEdit(note, index)}
                                    title={t('order.clickToEdit')}
                                  />
                                )}
                                {note.id === null && (
                                  <i
                                    className="bx bx-trash-alt text-muted"
                                    style={{ cursor: 'pointer', opacity: 0.6 }}
                                    onClick={e => {
                                      e.stopPropagation();
                                      deleteNote(index);
                                    }}
                                    title={t('common.delete')}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Note Section */}
          {!showInput ? (
            <Button
              onClick={() => setShowInput(true)}
              color="link"
              className="p-0 mt-2 text-decoration-none"
              style={{ fontSize: '14px' }}
            >
              <i className="bx bx-plus me-1" />
              {t('order.addNote')}
            </Button>
          ) : (
            <div style={{ marginTop: '12px', marginLeft: '48px' }}>
              <Card className="shadow-sm border-primary">
                <CardBody>
                  <textarea
                    value={newNoteText}
                    onChange={e => setNewNoteText(e.target.value)}
                    placeholder={t('order.enterNote')}
                    autoFocus
                    rows={4}
                    maxLength={255}
                    style={{
                      width: '100%',
                      padding: '8px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      marginBottom: '4px',
                    }}
                  />
                  <div
                    style={{
                      fontSize: '11px',
                      color: newNoteText.length === 255 ? '#f44336' : '#999',
                      textAlign: 'right',
                      marginBottom: '8px',
                    }}
                  >
                    {newNoteText.length} / 255 {t('common.characters')}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={() => {
                        setShowInput(false);
                        setNewNoteText('');
                      }}
                      size="sm"
                      color="secondary"
                      outline
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNoteText.trim()}
                      size="sm"
                      color="primary"
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          )}
        </CardBody>
      )}
    </Card>
  );
};
